import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';

import App from './App.vue';
import { router } from './router';
import { installGlobalErrorHandlers, trackError } from './utils/error-tracking';
import { initPwaInstall } from './composables/usePwaInstall';
import { initAppUpdate, recoverFromChunkError } from './composables/useAppUpdate';

import 'primeicons/primeicons.css';
import './design-system/tokens.css';
import './design-system/app.css';
import './design-system/landing.css';
import './design-system/prototype.css';
import './design-system/views.css';
// CRM port (Fase 0): tokens de sistema scopeados en .crm-scope + clases base s15-*
// del diseño recuperado de crm.wiservet.com. Namespaced (s15-/.crm-scope) => inertes
// fuera de las vistas CRM, cero impacto en el tema actual. Ver docs/plans/2026-09-20-crm-port-*.
import './design-system/crm-tokens.css';
import './design-system/crm-base.css';
import './assets/main.css';

// Fase 1.5: handlers globales errors browser (window.error +
// unhandledrejection). Por ahora persiste en localStorage; cuando se
// aprovisione Sentry account + DSN, error-tracking.ts se reemplaza por
// SDK Sentry.init() sin tocar el resto del código.
installGlobalErrorHandlers();

// D75+2: Vite despacha `vite:preloadError` en `window` cuando falla la
// carga de un chunk dinámico (JS o CSS) — típicamente porque el navegador
// tiene cacheado un index.html viejo que apunta a archivos con hash que ya
// no existen tras un deploy (dist se reemplaza atómicamente, sin mantener
// versiones viejas). Antes esto quedaba como error crudo visible al
// usuario ("Unable to preload CSS...", varios reportados en
// /admin/eventos). Recargar una sola vez por sesión (sessionStorage como
// guard) resuelve el caso normal sin arriesgar un loop si el problema es
// real y no solo cache vieja.
//
// D75+5: el reload plano de D75+2 seguía sin resolver el caso cuando el
// service worker de la PWA todavía controla la página con la versión VIEJA —
// su `NavigationRoute` sirve `index.html` desde el cache storage del SW, sin
// tocar red, así que el reload vuelve a caer en el mismo index.html
// desactualizado (mismo error repetido, visto en /admin/eventos: "text/html
// is not a valid JavaScript MIME type" con occurrence_count=26 en una sola
// sesión real, 2026-07-09).
//
// D77: la recuperación vive ahora en recoverFromChunkError (useAppUpdate.ts)
// porque el SW pasó de `autoUpdate` a `prompt`: ya NO hace skipWaiting solo —
// hay que postearle SKIP_WAITING explícitamente al SW en espera, o el reload
// volvería a caer en el precache viejo por siempre. Conserva el fallback a
// los 4.5s de PR #109 y el reporte a app_events. El guard 1×/sesión de acá
// evita loops si el error es real y no solo cache vieja.
const PRELOAD_ERROR_RELOAD_KEY = 'wiservet:preload-error-reloaded';
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY)) return;
  sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, '1');
  void recoverFromChunkError('chunk-error');
});

// Captura temprana del evento `beforeinstallprompt` para poder ofrecer la
// instalación de la PWA "a demanda" (botón en el sidebar), no solo el banner
// automático del navegador que aparece una vez.
initPwaInstall();

// D77: registro del SW + chequeo periódico de versión nueva + aplicación
// segura del update (próximo cambio de ruta, o toast a los 5 min sin
// navegar). Reemplaza el registro automático que inyectaba vite-plugin-pwa.
initAppUpdate(router);

const app = createApp(App);

// Vue 3 errorHandler — captura errores en lifecycle hooks + render.
app.config.errorHandler = (err, instance, info) => {
  trackError(err, {
    source: 'vue',
    componentName: instance?.$options.name ?? 'unknown',
    info,
  });
};

app.use(createPinia());
app.use(router);
app.use(PrimeVue, {
  ripple: true,
  theme: {
    preset: undefined,
  },
  unstyled: true,
});

app.mount('#app');
